import { Divider, Grid, InputAdornment, styled } from '@material-ui/core';
import { ToggleButtonGroup } from '@material-ui/lab';
import MuiToggleButton from '@material-ui/lab/ToggleButton';
import { AutoComplete, Dialog, Icons, TextField, Typography } from '@nutrien/cxp-components';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { emailNotificationReAssignByFI } from '../../graphql';
import { bulkAssignOrReassign } from '../../graphql/InspectorOperations';
import { useMst } from '../../mobx-models/Root';
import { IFieldInspectorList, INewInspector, IViewInspectorDocument } from '../../models/models';
import type { TaskList } from '../../rxdb/taskList/taskList.types';
import { useRxDB } from '../../rxdb/useRxDB';
import { isJestTest } from '../../test-helpers/isJestTest';
import { mockFieldInspectorList } from '../../test-helpers/mockData/assignInspector';
import { sentryCaptureException } from '../../utilities/sentryCaptureException';
import { useNotification } from '../../utilities/useNotification';
import { INSPECTION_LENGTH_WHILE_BULK } from '../../utilities/constants';
import { BatchSize, FieldInspectorType, InspectorRole, TaskStatus } from '../../utilities/enums';
import { trackEventFn, TrackingEventType } from '../../utilities/hooks/useEventTracking';
import { sortByInspectorName } from '../../utilities/sortHelper';
import CustomButton from '../CustomButton/CustomButton';
import StyledButton from '../InspectorInfo/StyledButton';
import useStyles from './ReAssignInspector.styles';

const ToggleButton = styled(MuiToggleButton)(({}) => ({
  '&.Mui-selected:not(.Mui-disabled), &.Mui-selected:hover:not(.Mui-disabled)': {
    color: '#121212',
    backgroundColor: 'rgba(42, 171, 63, 1)',
  },
  '&.Mui-selected, &.Mui-selected:hover': {
    color: '#121212',
    backgroundColor: 'dimgray',
  },
}));

interface Props {
  openAssignInspector: boolean;
  setOpenAssignInspector: (val: boolean) => void;
  fieldInspectorList: IViewInspectorDocument[];
  taskId: number[];
  taskList: TaskList[];
  isAssignToLead: boolean;
}

const BulkReAssignInspector: React.FC<Props> = ({
  openAssignInspector,
  setOpenAssignInspector,
  fieldInspectorList,
  taskId,
  taskList,
  isAssignToLead,
}: Props) => {
  const classes = useStyles();
  const { primaryContext, HandleGesture, CurrentUserSelected, SiteDiscipline } = useMst();
  const { db } = useRxDB();
  const { successNotification, errorNotification } = useNotification();

  const [selectedInspector, setSelectedInspector] = useState<INewInspector[]>([]);
  const [updatedInspector, setUpdatedInspector] = useState<{
    fieldInspectorId: number;
    fieldInspectorName: string;
    clerkId: number;
    clerkName: string;
  }>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [taskData, setTaskData] = useState<TaskList[]>([]);
  const [comment, setComment] = useState<string>('');
  const [inspectorType, setInspectorType] = useState<string>(FieldInspectorType.FI);
  const [inspectorListToRender, setInspectorListToRender] = useState<IViewInspectorDocument[]>([]);

  const loggedInUser = CurrentUserSelected?.currentUser;
  const [isDECView, setIsDECView] = useState(false);
  const [disableToggleButton] = useState(false);
  const isDECUser = !!loggedInUser?.clerkId;
  const allInspectorLookUp: IFieldInspectorList[] = isJestTest()
    ? mockFieldInspectorList
    : isDECView
    ? primaryContext.dataEntryClerksList.toJSON()
    : primaryContext.fieldInspectorList.toJSON();

  const handleToggleChange = (
    _event: React.MouseEvent<HTMLElement, MouseEvent>,
    inspType: string,
  ) => {
    if (inspType !== null) {
      setInspectorType(inspType);
    }
  };

  const setInspectorList = () => {
    const data = taskId
      ?.map(i => taskList?.filter((item: TaskList) => item.pkAssetInspectionTask === i))
      .flat();
    setTaskData(data);
    const obj = data
      ?.map((i: TaskList) => [
        {
          fieldInspectorName: i.name,
          fieldInspectorId: i.fkInspectorField,
          clerkName: i.clerkName,
          clerkId: i.clerkId,
        },
      ])
      .flat();
    const fieldInspectorlist = obj?.filter(
      (elem: INewInspector, index: number) =>
        obj.findIndex(
          obj1 => obj1.fieldInspectorId === elem.fieldInspectorId || obj1.clerkId === elem.clerkId,
        ) === index,
    );
    setSelectedInspector(fieldInspectorlist);
  };

  const selectedFilteredInspectors = useMemo(() => {
    return selectedInspector?.filter(item => {
      if (inspectorType === FieldInspectorType.DEC) {
        return item.clerkId;
      } else {
        return item.fieldInspectorId;
      }
    });
  }, [selectedInspector, inspectorType]);

  useEffect(() => {
    if (!isDECUser) {
      const isDECViewSelected = inspectorType === FieldInspectorType.DEC;
      setIsDECView(isDECViewSelected);
    }
  }, [inspectorType, selectedInspector]);

  const setInspector = (inspectorId: number, name: string, clerkId: number, clerkName: string) => {
    setUpdatedInspector({
      fieldInspectorId: inspectorId,
      fieldInspectorName: name,
      clerkId,
      clerkName,
    });
  };

  const onCommentsInput = (event: React.FocusEvent<HTMLInputElement>) => {
    setComment(event.target.value);
  };

  const onAssign = async () => {
    setIsLoading(true);
    const isReassignmentByFI = !isAssignToLead;

    const mobileFieldInspectorInspections = {
      pkAssetInspectionTask: 1,
      fkSchedule: CurrentUserSelected.workPackageId,
      fkInspectorLead: taskData[0].fkInspectorLead,
      fkInspectorField: Number(updatedInspector?.fieldInspectorId || 0),
      taskDescription: comment,
      isReviewedByLI: false,
      isReviewedByFI: false,
      commentInspectorLead: '',
      commentInspectorField: '',
      site: SiteDiscipline.siteId,
      discipline: SiteDiscipline.disciplineId,
      isActive: true,
      fkInspectionTaskStatusCategory: TaskStatus.ASSIGNED,
      pkUuId: uuidv4(),
      recommendations: '',
      dateCompleted: null,
      isPerformedByFI: isReassignmentByFI,
      isBulkReAssignment: true,
      clerkId: Number(updatedInspector?.clerkId || 0),
    };
    const role = isReassignmentByFI ? InspectorRole.FIELD : InspectorRole.LEAD;

    const varialblesList = [];
    let position = 0;
    while (position < taskData.length) {
      const batch = taskData.slice(position, position + INSPECTION_LENGTH_WHILE_BULK);
      const inspectionIdList: number[] = [];
      batch.forEach(task => {
        if (!inspectionIdList.includes(task.fkAssetInspection))
          inspectionIdList.push(task.fkAssetInspection);
      });
      const taskIdList = batch.map(task => task.pkAssetInspectionTask);
      varialblesList.push({
        mobileFieldInspectorInspections,
        role,
        inspectionIdList,
        taskIdList,
      });
      position += INSPECTION_LENGTH_WHILE_BULK;
    }

    try {
      position = 0;
      let results: unknown[] = [];
      while (position < varialblesList.length) {
        const itemsForBatch = varialblesList.slice(position, position + BatchSize.RE_ASSIGN);
        results = [
          ...results,
          ...(await Promise.all(
            itemsForBatch.map(async variables => {
              try {
                await bulkAssignOrReassign(variables);
              } catch (error) {
                // eslint-disable-next-line no-console
                console.error(error);
                // console logs will be removed once sentry implementation is done
                sentryCaptureException(
                  error,
                  '~ file: AssignToMeDialog.tsx ~ line 120 ~ saveBulkTaskOnInspection ~',
                  'bulkOperation',
                  JSON.stringify({ ...variables, error: JSON.stringify(error) }),
                );
              }
            }),
          )),
        ];
        position += BatchSize.RE_ASSIGN;
      }
      if (results.length === varialblesList.length) {
        const payload = HandleGesture.tasksforReAssignAndSign.map(task => {
          return {
            inspectionId: task.inspectionId,
            taskId: task.taskId,
            previousFI: task.fieldInspectorId,
          };
        });
        await emailNotificationReAssignByFI(
          CurrentUserSelected.workPackageId,
          Number(updatedInspector?.fieldInspectorId || 0),
          taskData[0]?.fkInspectorLead,
          Number(updatedInspector?.clerkId || 0),
          payload,
          isReassignmentByFI,
        );
      }

      db?.startReplication();
      successNotification(
        `${!isDECView ? 'Field Inspector' : 'Data Entry Clerk'} re-assigned Successfully`,
      );
      setIsLoading(false);
      setOpenAssignInspector(false);
      HandleGesture.resetEverything();
      HandleGesture.setIsCompleted(true);
      HandleGesture.setRunOnTaskSubscription(true);
    } catch (error) {
      errorNotification(
        `${!isDECView ? 'Field Inspector' : 'Data Entry Clerk'} is not updated please try again`,
      );
      setIsLoading(false);
      HandleGesture.resetEverything();
    }
  };

  const handleOnSubmit = (event: React.KeyboardEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (updatedInspector?.fieldInspectorId || updatedInspector?.clerkId) {
      onAssign();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (
      event.key === 'Enter' &&
      (updatedInspector?.fieldInspectorId || updatedInspector?.clerkId)
    ) {
      onAssign();
    }
  };

  const handleSearchForAssign = (
    event: React.KeyboardEvent<HTMLDivElement>,
    value: {
      fieldInspectorId: number;
      fieldInspectorName: string;
      clerkId: number;
      clerkName: string;
    },
  ) => {
    if (
      event.key === 'Enter' &&
      (updatedInspector?.fieldInspectorId || updatedInspector?.clerkId)
    ) {
      onAssign();
      return;
    }
    if (
      (value?.fieldInspectorId && value?.fieldInspectorId === loggedInUser?.inspectorId) ||
      (value?.clerkId && value?.clerkId === loggedInUser?.clerkId)
    ) {
      setInspector(
        loggedInUser.inspectorId,
        loggedInUser.name,
        loggedInUser.clerkId,
        loggedInUser.name,
      );
    } else {
      setInspector(
        value?.fieldInspectorId,
        value?.fieldInspectorName,
        value?.clerkId,
        value?.clerkName,
      );
    }
  };

  useEffect(() => {
    let isMounted = true;
    const getMasterData = async () => {
      try {
        if (isMounted) {
          await primaryContext.getFIListFromMobx();
          await primaryContext.getDataEntryClerksFromMobx();
        }
      } catch (error) {
        if (isMounted) {
          errorNotification('Inspector data is not loaded');
          await trackEventFn(TrackingEventType.SERVICE_FAILED, {
            error: JSON.stringify(error),
          });
        }
      }
    };
    getMasterData();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setInspectorList();
  }, []);

  useEffect(() => {
    const getLeadInspectorData = () => {
      if (taskData && fieldInspectorList) {
        const leadInspectorId = taskData[0]?.fkInspectorLead;
        const clerkId = taskData[0]?.clerkId;
        const leadInspector = allInspectorLookUp.find(inspector => {
          if (!isDECView) {
            return inspector.pkInspector === leadInspectorId;
          } else {
            return true;
          }
        });
        setInspectorListToRender([]);
        if (leadInspector) {
          const flag = fieldInspectorList.find(inspector => {
            if (!isDECView) {
              return inspector.fieldInspectorId === leadInspectorId;
            } else {
              return false;
            }
          });
          if (!flag) {
            const lookUps = [...fieldInspectorList];
            lookUps.push({
              fieldInspectorId: leadInspectorId,
              fieldInspectorName: `${leadInspector?.firstName} ${leadInspector?.lastName}`,
              clerkName: leadInspector.clerkName || '',
              clerkId,
            });
            if (!isDECView) {
              lookUps.forEach((inspector: IViewInspectorDocument, index: number) => {
                if (loggedInUser?.inspectorId === inspector.fieldInspectorId)
                  lookUps[index].fieldInspectorName = 'Assigned to Me';
              });
            }

            setInspectorListToRender(
              lookUps
                .filter(
                  lookUp =>
                    !selectedInspector.find((selected: INewInspector) => {
                      if (!isDECView) {
                        return (
                          selected.fieldInspectorId &&
                          lookUp.fieldInspectorId === selected.fieldInspectorId
                        );
                      } else {
                        return lookUp.clerkId === selected.clerkId;
                      }
                    }),
                )
                ?.sort(sortByInspectorName),
            );
          }
        }
      }
    };
    getLeadInspectorData();
  }, [fieldInspectorList, allInspectorLookUp, taskData]);

  const getDivider = () => {
    return <Divider orientation="horizontal" className={classes.divider} />;
  };

  return (
    <Dialog
      open={openAssignInspector}
      titleText={`Re-Assign ${!isDECView ? 'Inspector' : 'Data Entry Clerk'}`}
      onClose={() => !isLoading && setOpenAssignInspector(false)}
      backButton={false}
      dialogActions={
        <CustomButton
          isLoading={isLoading}
          onClose={() => !isLoading && onAssign()}
          disabled={!updatedInspector?.fieldInspectorId && !updatedInspector?.clerkId}
          buttonText="Re-Assign"
        />
      }
      maxWidth="xs"
      noMinHeight
      disableEnforceFocus={isLoading ? true : undefined}
      BackdropProps={{
        onClick: event => {
          if (isLoading) event.preventDefault();
        },
        onTouchStart: event => {
          if (isLoading) event.preventDefault();
        },
        style: { touchAction: isLoading ? 'none' : 'auto' },
      }}
      data-testid="dialog-id"
    >
      <form onSubmit={handleOnSubmit} data-testid="formId">
        <Grid container className={classes.root}>
          {selectedInspector && Object.keys(selectedInspector)?.length > 0 && (
            <Grid item container xs={12} alignItems="center">
              <ToggleButtonGroup
                color="#121212"
                value={inspectorType}
                exclusive
                onChange={handleToggleChange}
              >
                <ToggleButton
                  disabled={disableToggleButton}
                  value={FieldInspectorType.FI}
                  data-testid="nutrientogglebutton"
                >
                  Field Inspector
                </ToggleButton>
                <ToggleButton
                  disabled={disableToggleButton}
                  value={FieldInspectorType.DEC}
                  data-testid="sptogglebutton"
                >
                  Data Entry Clerk
                </ToggleButton>
              </ToggleButtonGroup>
              {selectedFilteredInspectors && selectedFilteredInspectors.length > 0 && (
                <>
                  {getDivider()}
                  <Grid item container alignItems="center">
                    <Typography variant="body2" className={classes.nameLabel}>
                      Assigned {!isDECView ? 'Inspector' : 'Data Entry Clerk'}
                    </Typography>
                  </Grid>
                  <Grid item container>
                    {selectedFilteredInspectors.map((inspector: INewInspector) => (
                      <StyledButton inspector={inspector} canDelete={false} data-testid="FIlist" />
                    ))}
                  </Grid>
                </>
              )}
            </Grid>
          )}

          {getDivider()}

          <Grid item container>
            <Typography variant="subtitle2" className="reassignInspectorLabel">
              Assign {!isDECView ? 'Inspector' : 'Data Entry Clerk'}
            </Typography>
            <AutoComplete
              noMinHeight
              size="small"
              closeIcon=""
              disabled={isLoading}
              startAdornment={
                <InputAdornment position="start">
                  <Icons.SearchFeather
                    color="rgba(149, 149, 149, 1)"
                    className={classes.searchIcon}
                  />
                </InputAdornment>
              }
              endAdornment={
                <InputAdornment position="end">
                  <Icons.PlusCircleFeather
                    className={classes.plusIcon}
                    color="primary"
                    strokeWidth="1.5px"
                  />
                </InputAdornment>
              }
              placeholder="Search"
              onChange={(event, newValue) => {
                if (!isLoading) handleSearchForAssign(event, newValue);
              }}
              list={inspectorListToRender}
              value=""
              getOptionLabel={option => option?.fieldInspectorName || option?.clerkName || ''}
              autoSelect={false}
              data-testid="assignInspectorList"
            />
          </Grid>

          <Grid item container className={classes.commentPadding}>
            <Typography variant="subtitle2" className="reassignInspectorLabel">
              Comments
            </Typography>
            <TextField
              onChange={onCommentsInput}
              noMinHeight
              fullWidth
              disabled={isLoading}
              variant="outlined"
              size="small"
              placeholder="Type"
              multiline
              rows={3}
              onKeyDown={handleKeyDown}
              inputProps={{ 'data-testid': 'commentbox' }}
            />
          </Grid>
        </Grid>
      </form>
    </Dialog>
  );
};

export default observer(BulkReAssignInspector);
